import React, { useState, FormEvent, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../store/hook"
import { DateRangePicker } from "react-date-range"
import CustomModal from "../common/CustomModal"
import {
  fetchReconReport,
  clearReconReport,
  fetchDistributors,
} from "../../features/distributor/DistributorReconSlice"
import { RootState } from "../../store/store"
import { Loader2, AlertCircle, Download } from "lucide-react"
import * as XLSX from "xlsx"
import axios from "axios"
import { useMoney } from "../../hooks/useMoney"




const CreateDistributorReconReport: React.FC = () => {
  const dispatch = useAppDispatch()
  const { symbol, code, locale, format } = useMoney()

  const now = new Date()
  const lastMonth = new Date()
  lastMonth.setMonth(lastMonth.getMonth() - 1)

  const [dateRange, setDateRange] = useState([
    {
      startDate: lastMonth,
      endDate: now,
      key: "selection",
    },
  ])

  const [distributorId, setDistributorId] = useState<number | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [formErrors, setFormErrors] = useState({
    dateRangeError: "",
    distributorIdError: "",
  })

  const {
    loading,
    error,
    reconReport,
    distributors,
    distributorError,
    distributorLoading,
  } = useAppSelector((state: RootState) => state.distributorRecon)

  useEffect(() => {
    dispatch(fetchDistributors())
  }, [dispatch])

  // Clear recon report if inputs change
  const handleFormChange = () => {
    // Reset error states
    setFormErrors({
      distributorIdError: "",
      dateRangeError: "",
    })

    dispatch(clearReconReport())
  }

  const handleSelect = (ranges: any) => {
    setDateRange([ranges.selection])
    handleFormChange()
  }

  // Helper function to format date in local timezone (YYYY-MM-DD)
  const formatDateToLocalString = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Format date range for display
  const formatDateRange = () => {
    const { startDate, endDate } = dateRange[0]
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()
    
    if (startYear === endYear) {
      // Same year - don't show year
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    } else {
      // Different years - show year
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    }
  }

  // Handling the form submission
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Reset error states
    setFormErrors({
      distributorIdError: "",
      dateRangeError: "",
    })

    // Validate date range
    if (!dateRange[0].startDate || !dateRange[0].endDate) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        dateRangeError: "Please select a date range.",
      }))
    }

    // Validate distributor_id
    if (!distributorId) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        distributorIdError: "Please select a distributor.",
      }))
    }

    // If the form is invalid, return early
    if (!dateRange[0].startDate || !dateRange[0].endDate || !distributorId) return

    const formattedFromDate = formatDateToLocalString(dateRange[0].startDate)
    const formattedToDate = formatDateToLocalString(dateRange[0].endDate)

    try {
      // Dispatching the thunk to fetch recon report
      dispatch(
        fetchReconReport({
          distributorId: distributorId,
          fromDate: formattedFromDate,
          toDate: formattedToDate,
        }),
      )
      // Clear any previous errors
      setFormErrors({
        dateRangeError: "",
        distributorIdError: "",
      })
    } catch (error) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        dateRangeError: "Failed to create recon report. Please try again.",
      }))
    }
  }

  function formatCurrency(amount: number, currency: string): string {
    let options: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }

    // Apply currency formatting (USD or others)
    const formatter = new Intl.NumberFormat('en-US', options)

    if (currency === code && locale === 'en-IN') {
      // Custom format for Indian-style digit grouping
      let formatted = amount ? amount.toFixed(2) : "" // Ensure two decimal points
      let parts = formatted.split('.')
      let integerPart = parts[0]
      let decimalPart = parts[1]

      // Format the integer part in the Indian numbering system
      let lastThree = integerPart.slice(-3)
      let otherNumbers = integerPart.slice(0, integerPart.length - 3)
      if (otherNumbers.length > 0) {
        lastThree = ',' + lastThree
      }
      let indianFormatted = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree

      // Combine with the decimal part
      return indianFormatted ? `${symbol}${indianFormatted}.${decimalPart}` : `${symbol}0.00`
    }

    if (currency === code) return format(amount)

    // Default currency formatting for others
    return formatter.format(amount) 
  }

  function convertBrandData(data: any) {
    const result = []

    for (const brand in data.brands.distributor) {
      const distributorData = data.brands.distributor[brand]
      const fincentiveData = data.brands.fincentive[brand]
      const dv = formatCurrency(distributorData.orderValue, distributorData.currency)
      const fv = formatCurrency(fincentiveData.orderValue, distributorData.currency)
      const brandArray = [
        {
          head: brand,
          distributor: "Distributor",
          fincentive: "Fincentive",
        },
        {
          head: "Orders",
          distributor: distributorData.voucherRequests,
          fincentive: fincentiveData ? fincentiveData.voucherRequests : 0,
        },
        {
          head: "Vouchers",
          distributor: distributorData.vouchers,
          fincentive: fincentiveData ? fincentiveData.vouchers : 0,
        },
        {
          head: "Order Values",
          distributor: dv,
          fincentive: fincentiveData
            ? fv
            : 0,
        },
      ]

      result.push(brandArray)
    }

    return result
  }

  function saveJsonToExcel(data: any) {
    // Step 1: Prepare data for each sheet
    const voucherRequests: any = [];
    const vouchers: any = [];
    const orderCounts: any = [];

    // Step 2: Extract and format data from JSON
    Object.values(data).forEach((requestArray: any) => {
      requestArray.forEach((requestObj: any) => {
        const voucherRequest = requestObj.voucherRequest;
        voucherRequests.push({
          voucherRequestId: voucherRequest.voucherRequestId,
          programId: voucherRequest.programId,
          userId: voucherRequest.userId,
          requestDate: voucherRequest.requestDate,
          quantity: voucherRequest.quantity,
          denominationAmount: voucherRequest.denominationAmount || voucherRequest.denomination_amount,
          status: voucherRequest.status,
          amount: voucherRequest?.apiRequestPayload?.amount || '',
          apiUserId: voucherRequest?.apiRequestPayload?.userId || '',
          brandId: voucherRequest?.apiRequestPayload?.brandId || '',
          currency: voucherRequest?.apiRequestPayload?.currency || '',
          apiQuantity: voucherRequest?.apiRequestPayload?.quantity || '',
          referenceId: voucherRequest?.apiRequestPayload?.referenceId || '',
          receiverName: voucherRequest?.apiRequestPayload?.receiverName || '',
          distributorId: voucherRequest?.apiRequestPayload?.distributorId || '',
          receiverEmail: voucherRequest?.apiRequestPayload?.receiverEmail || '',
          receiverPhone: voucherRequest?.apiRequestPayload?.receiverPhone || '',
          responsePayload: voucherRequest?.apiResponsePayload || '',
          orderId: voucherRequest?.orderId || '',
          createdAt: voucherRequest?.createdAt || '',
          updatedAt: voucherRequest?.updatedAt || '',
        });

        // Add vouchers
        requestObj.vouchers.forEach((voucher: any) => {
          vouchers.push({
            voucherId: voucher.voucherId,
            voucherRequestId: voucher.voucherRequestId,
            programId: voucher.programId,
            programCode: voucher.programCode,
            voucherCreateDate: voucher.voucherCreateDate,
            voucherUuid: voucher.voucherUuid,
            issuerBankId: voucher.issuerBankId,
            bankApiResponsePayload: voucher.bankApiResponsePayload,
            voucherRef: voucher.voucherRef,
            saralPin: voucher.saralPin,
            saralId: voucher.saralId,
            code: voucher.code,
            pin: voucher.pin,
            url: voucher.url,
            senderId: voucher.senderId,
            senderEmail: voucher.senderEmail,
            senderName: voucher.senderName,
            referenceId: voucher.referenceId,
            senderPhone: voucher.senderPhone,
            receiverEmail: voucher.receiverEmail,
            receiverPhone: voucher.receiverPhone,
            receiver_name: voucher.receiver_name,
            state: voucher.state,
            channel: voucher.channel,
            deliveryMethod: voucher.deliveryMethod,
            language: voucher.language,
            countryCode: voucher.countryCode,
            amount: voucher.amount,
            currency: voucher.currency,
            currencyConversionRate: voucher.currencyConversionRate,
            redemptionReferenceNumber: voucher.redemptionReferenceNumber,
            dueDate: voucher.dueDate,
            expiryDate: voucher.expiryDate,
            dateSent: voucher.dateSent,
            dateAccepted: voucher.dateAccepted,
            dateRedeemed: voucher.dateRedeemed,
            dateSold: voucher.dateSold,
            dateCancelled: voucher.dateCancelled,
            reconStatus: voucher.reconStatus,
            reconDisplayStatus: voucher.reconDisplayStatus,
            reconMessage: voucher.reconMessage,
            reconDate: voucher.reconDate,
            reconPerformedAt: voucher.reconPerformedAt,
            createdAt: voucher.createdAt,
            updatedAt: voucher.updatedAt,
          });
        });

      });
    });


    // Add order counts
    orderCounts.push({
      successCount: reconReport?.counts.success,
      inProgressCount: reconReport?.counts.in_progress,
      failedCount: reconReport?.counts.failed,
    });
    // Step 3: Convert JSON arrays to worksheet
    const workbook = XLSX.utils.book_new();
    const voucherRequestSheet = XLSX.utils.json_to_sheet(voucherRequests);
    const voucherSheet = XLSX.utils.json_to_sheet(vouchers);
    const orderCountSheet = XLSX.utils.json_to_sheet(orderCounts);

    // Step 4: Append worksheets to workbook
    XLSX.utils.book_append_sheet(workbook, voucherRequestSheet, "VoucherRequests");
    XLSX.utils.book_append_sheet(workbook, voucherSheet, "Vouchers");
    XLSX.utils.book_append_sheet(workbook, orderCountSheet, "OrderCounts");

    // Step 5: Write the workbook to a file and trigger download
    XLSX.writeFile(workbook, "VoucherData.xlsx");
  }
  const handleDownload = async () => {
    try {
          const formattedFromDate = formatDateToLocalString(dateRange[0].startDate)
    const formattedToDate = formatDateToLocalString(dateRange[0].endDate)
      const token = localStorage.getItem('accessToken')

      const response = await axios.post(`${window.env.API_URL}/v1/utility/recon/distributor/transactionreport`, {
        distributorId,
        toDate: formattedToDate,
        fromDate: formattedFromDate
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      // Check if the response was successful
      if (response.status === 200) {
        saveJsonToExcel(response.data)
      }
    } catch (error) {
      console.error("Error during POST request:", error)
    }
  }

  if (distributorLoading)
    return (
      <div className="bg-white min-h-screen">
        <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
          <div className="flex items-center h-10">
            <h2 className="text-2xl font-bold text-gray-900">
              Distributor Report
            </h2>
          </div>
        </div>
        <div className="flex justify-center items-center h-[calc(100vh-20rem)]">
          <div className="flex items-center gap-2">
            <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        </div>
      </div>
    )

  if (distributorError)
    return (
      <div className="bg-white min-h-screen">
        <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
          <div className="flex items-center h-10">
            <h2 className="text-2xl font-bold text-gray-900">
              Distributor Report
            </h2>
          </div>
        </div>
        <div className="flex justify-center items-center h-[calc(100vh-20rem)]">
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <span>{distributorError ? distributorError : "Something went wrong!"}</span>
          </div>
        </div>
      </div>
    )

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center h-10">
          <h2 className="text-2xl font-bold text-gray-900">
            Distributor Report
          </h2>
        </div>
        <div className="flex items-center h-10">
          <button
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-500 bg-white hover:shadow-sm"
            onClick={() => setIsModalOpen(true)}
          >
            <span>{formatDateRange()}</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
          <form className="w-full" onChange={handleFormChange} onSubmit={handleSubmit}>
            {/* Distributor Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Distributor
              </label>
              <div className="relative">
                <select
                  onChange={(e) => setDistributorId(Number(e.target.value))}
                  className={`block w-full px-3 py-2 border ${formErrors.distributorIdError ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                >
                  <option value="">Select Distributor</option>
                  {distributors?.map((row: any) => (
                    <option key={row.distributorId} value={row.distributorId}>
                      {row.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {formErrors.distributorIdError && (
                  <div className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="mr-1" />
                    {formErrors.distributorIdError}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex">
              <button
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </div>
                ) : (
                  "Start Recon Process"
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Date Range Modal */}
        <CustomModal
          show={isModalOpen}
          onHide={() => setIsModalOpen(false)}
          title="Date Range"
          width="w-[600px]"
        >
          <div className="flex flex-col items-center">
            <DateRangePicker
              ranges={dateRange}
              onChange={handleSelect}
              maxDate={new Date()}
            />
          </div>
        </CustomModal>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error ? error : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Report Results */}
        {reconReport && (
          <div className="space-y-8 mb-8">
            {/* Order Summary */}
            <div className="bg-white rounded-xl p-6 flex flex-col justify-start transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
              <div className="font-semibold text-lg mb-6">Order Summary</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Success:</span>
                  <span className="text-lg font-bold text-green-600">{reconReport.counts.success}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">In Progress:</span>
                  <span className="text-lg font-bold text-yellow-600">{reconReport.counts.in_progress}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Failed:</span>
                  <span className="text-lg font-bold text-red-600">{reconReport.counts.failed}</span>
                </div>
              </div>
            </div>

            {/* Transaction Summary and Wallet Balance */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 " >
              {/* Transaction Summary */}
              <div className="lg:col-span-2 bg-white rounded-xl p-6 flex flex-col justify-start transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
                <div className="flex justify-between items-center mb-6">
                  <div className="font-semibold text-lg">Transaction Summary</div>
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
                {convertBrandData(reconReport).map((brandData, brandIndex) => (
                  <div key={brandIndex} className="mb-6">
                    {brandData.map((outlet, index) => (
                      <div
                        key={index}
                        className={`flex justify-between items-center py-3 ${index !== brandData.length - 1 ? 'border-b border-gray-200' : ''}`}
                      >
                        <span
                          className={`text-sm ${index === 0 ? 'font-bold text-green-600' : 'text-gray-700'}`}
                        >
                          {outlet.head}
                        </span>
                        <span
                          className={`text-sm ${index === 0 ? 'font-bold' : ''}`}
                        >
                          {typeof outlet.distributor === 'string' ? outlet.distributor : outlet.distributor.toLocaleString()}
                        </span>
                        <span
                          className={`text-sm ${index === 0 ? 'font-bold' : ''}`}
                        >
                          {typeof outlet.fincentive === 'string' ? outlet.fincentive : outlet.fincentive.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Wallet Balance */}
              <div className="bg-white h-fit rounded-xl p-6 flex flex-col justify-start transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
                <div className="font-semibold text-lg mb-6">Wallet Balance</div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Initial Balance:</span>
                    <span className="text-sm text-gray-500">- {code}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Distributor Order Value:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(reconReport.distributorOrderValue, code)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Voucher Creation Value:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(reconReport.voucherCreationValue, code)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CreateDistributorReconReport
